import * as Ably from 'ably'
import { supabase } from '@/lib/supabase'
import { logger } from '@/lib/utils/logger'
import { config } from '@/lib/config'

const API_BASE_URL = config.apiURL || 'http://localhost:8000'

export interface AblyTokenDetails {
    token: string
    expires: number
    issued: number
    clientId: string
    capability: string
}

/**
 * Fetches a scoped, subscribe-only Ably token from the backend.
 * Called by the Ably authCallback — the SDK handles renewal automatically
 * before token expiry (1 hour TTL).
 */
export async function fetchAblyToken(agentId: string): Promise<AblyTokenDetails> {
    const { data: { session }, error } = await supabase.auth.getSession()

    if (error || !session?.access_token) {
        throw new Error('Not authenticated')
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/ably/auth`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ agent_id: agentId }),
    })

    if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        const msg = (body as { detail?: string })?.detail ?? `Ably auth failed: ${response.status}`
        logger.error('[AblyService] Token fetch failed', { agentId, status: response.status, msg })
        throw new Error(msg)
    }

    return response.json() as Promise<AblyTokenDetails>
}

/**
 * Creates an Ably Realtime client scoped to one agent.
 * Uses authCallback so the SDK automatically renews the token before expiry.
 * The client never holds the Ably API key — only short-lived scoped tokens.
 */
export function createAblyClient(
    agentId: string,
    initialTokenDetails?: AblyTokenDetails
): Ably.Realtime {
    return new Ably.Realtime({
        tokenDetails: initialTokenDetails as unknown as Ably.TokenDetails | undefined,
        authCallback: async (_tokenParams, callback) => {
            try {
                const tokenDetails = await fetchAblyToken(agentId)
                callback(null, tokenDetails as unknown as Ably.TokenDetails)
            } catch (err) {
                logger.error('[AblyService] authCallback failed', err)
                callback(err instanceof Error ? err.message : String(err), null)
            }
        },
    })
}
